import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { DialogData } from '../../../domain/common.model';
import { SystemUser } from '../../../domain/user.model';
import { GrowlService } from '../service/growl.service';
import { UserService } from '../service/user.service';
import { AsideAction } from './aside.action';
import { DialogAction } from './dialog.action';
import { ErrorsAction } from './errors.state';
import { RouterAction } from './router.action';

import { UserAction } from './user.action';


export class UserStateModel {
    url: string;
    user: SystemUser;
    error: boolean;
}

@State<UserStateModel>({
    name: 'user',
    defaults: {
        url: null,
        user: null,
        error: false,
    }
})
@Injectable()
export class UserState {
    constructor(private userService: UserService,
                private growlService: GrowlService) {
    }

    @Selector()
    static getUser(state: UserStateModel) {
        return state.user;
    }

    @Selector()
    static getError(state: UserStateModel) {
        return state.error;
    }

    @Action(UserAction.Login)
    login(context: StateContext<UserStateModel>, action: UserAction.Login) {
        context.patchState({error: false});
        this.userService.login(action.request.username, action.request.password)
            .subscribe(user => {
                context.patchState({user, error: false});
                if (action.url) {
                    context.dispatch(new RouterAction.Navigate(action.url, {replaceUrl: true}));
                } else {
                    context.dispatch(new RouterAction.Navigate(['/'], {replaceUrl: true}));
                }
            }, (rejection: HttpErrorResponse) => {
                if (rejection.status === 401) {
                    context.patchState({error: true});
                    context.dispatch(new ErrorsAction.CleanMain());
                }
            });
    }

    @Action(UserAction.ChangeCharacter)
    changeCharacter(context: StateContext<UserStateModel>, action: UserAction.ChangeCharacter) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'MODAL.CHANGE_CHARACTER_CONFIRMATION';
        data.descriptionValues = {name: action.entity.name};
        data.onConfirm = () => {
            return this.userService.changeCharacter(action.entity.entityVersion);
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));

    }

    @Action(UserAction.Logout)
    logout(context: StateContext<UserStateModel>) {
        this.userService.logout()
            .subscribe(() => {
                context.dispatch([
                    new RouterAction.Navigate(['/login']),
                    new UserAction.ClearCurrentUser(),
                ]);
            });
    }

    @Action(UserAction.Unauthorized)
    unauthorized(context: StateContext<UserStateModel>, action: UserAction.Unauthorized) {
        const actions: any[] = [new RouterAction.Navigate(['/login'], {queryParams: {link: context.getState().url}}),]
        if (context.getState().user) {
            const data = new DialogData();
            data.titleLabel = 'LABEL.LOGGED_OUT_HEADER';
            data.descriptionLabel = 'LABEL.LOGGED_OUT_DESCRIPTION';
            data.hasCancelLabel = false;
            data.confirmLabel = 'BUTTON.OK';

            actions.push(new AsideAction.CloseAll());
            actions.push(new DialogAction.CloseAll());
            actions.push(new DialogAction.OpenGeneric(data));
        }
        context.dispatch(actions);
    }

    @Action(UserAction.ClearCurrentUser)
    clearUser(context: StateContext<UserStateModel>) {
        context.patchState({
            user: null
        });
    }

    @Action(UserAction.SetCurrentUser)
    setUser(context: StateContext<UserStateModel>, action: UserAction.SetCurrentUser) {
        context.patchState({user: action.user});
    }

    @Action(UserAction.SetURL)
    setURL(context: StateContext<UserStateModel>, action: UserAction.SetURL) {
        context.patchState({url: action.url});
    }

    @Action(UserAction.ForgotPassword)
    forgotPassword(context: StateContext<UserStateModel>, action: UserAction.ForgotPassword) {
        return this.userService.forgotPassword(action.username)
            .pipe(tap(() => {
                const data = new DialogData();
                data.titleLabel = 'LABEL.GROWL_SUCCESS_TITLE';
                data.descriptionLabel = 'LABEL.PASSWORD.RESET_DIALOG'
                data.hasCancelLabel = false;
                data.onConfirm = () => {
                    return context.dispatch(new RouterAction.Navigate(['/login']));
                };
                context.dispatch(new DialogAction.OpenGeneric(data));
            }));
    }

    @Action(UserAction.ResetPassword)
    resetPassword(context: StateContext<UserStateModel>, action: UserAction.ResetPassword) {
        return this.userService.resetPassword(action.token, action.password, action.password2)
            .pipe(
                tap(() => {
                    const data = new DialogData();
                    data.titleLabel = 'LABEL.GROWL_SUCCESS_TITLE';
                    data.descriptionLabel = 'LABEL.PASSWORD.RESET_DIALOG_SUCCESS'
                    data.hasCancelLabel = false;
                    data.onConfirm = () => {
                        return context.dispatch(new RouterAction.Navigate(['/login']));
                    };
                    context.dispatch(new DialogAction.OpenGeneric(data));
                }),
                catchError((err: any) => {
                    if (err instanceof HttpErrorResponse && err.status === 404) {
                        this.growlService.showErrorGrowl('ERROR.PASSWORD_CHANGE_FAILURE');
                    }
                    return throwError(err);
                })
            );
    }
}
